import { useEffect, useRef } from 'react';
import { useSignalR, useSignalRUpdates } from '@/components/providers/SignalRProvider';
import { useNotificationSound } from '@/hooks/useNotificationSound';

const CONNECT_GRACE_MS = 5000;

/**
 * Watches the SignalR-driven message and notification counts and plays the
 * user's selected chime whenever a count increases. Playback is fully
 * client-driven (we ignore the backend's `playSound` flag).
 *
 * To avoid chiming on the initial sync push that fires right after connect,
 * we suppress playback for `CONNECT_GRACE_MS` after `isConnected` flips true.
 *
 * Renders nothing. Mount once below `SignalRProvider`. Web-only; native
 * platforms use a no-op stub since chime playback is handled by the OS.
 */
export function NotificationSoundPlayer() {
  const { isConnected } = useSignalR();
  const { messageCount, notificationCount } = useSignalRUpdates();
  const { play } = useNotificationSound();

  const prevMessageCountRef = useRef<number>(0);
  const prevNotificationCountRef = useRef<number>(0);
  const connectedAtRef = useRef<number | null>(null);

  useEffect(() => {
    connectedAtRef.current = isConnected ? Date.now() : null;
  }, [isConnected]);

  useEffect(() => {
    const connectedAt = connectedAtRef.current;
    const inGrace = connectedAt !== null && Date.now() - connectedAt < CONNECT_GRACE_MS;

    if (!inGrace) {
      if (messageCount > prevMessageCountRef.current) play('message');
      if (notificationCount > prevNotificationCountRef.current) play('notification');
    }

    prevMessageCountRef.current = messageCount;
    prevNotificationCountRef.current = notificationCount;
  }, [messageCount, notificationCount, play]);

  return null;
}
